import type { ReactNode } from "react";
import { ApiError } from "../lib/api";
import { Button } from "./Button";
import { Icon } from "./Icon";

export function Skeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div className="skeleton" role="status" aria-live="polite">
      <span className="visually-hidden">Yükleniyor…</span>
      {Array.from({ length: lines }, (_, i) => (
        <div key={i} className="skeleton__line" style={{ width: `${90 - i * 12}%` }} />
      ))}
    </div>
  );
}

export function Empty({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <p className="empty__title">{title}</p>
      {children && <p className="empty__body">{children}</p>}
      {action && <div className="empty__action">{action}</div>}
    </div>
  );
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof ApiError ? error.message : "Beklenmeyen bir sorun oluştu. Lütfen tekrar deneyin.";
  return (
    <div className="error-box" role="alert">
      <Icon name="warning-circle" size={20} />
      <div>
        <p>{message}</p>
        {onRetry && (
          <Button size="sm" variant="secondary" icon="arrow-counter-clockwise" onClick={onRetry}>
            Tekrar dene
          </Button>
        )}
      </div>
    </div>
  );
}

/** Sorgu durumlarını tek yerde yönetir: yükleniyor / hata / veri. */
export function QueryView<T>({
  query,
  children,
  lines,
}: {
  query: { data: T | undefined; isPending: boolean; error: unknown; refetch: () => unknown };
  children: (data: T) => ReactNode;
  lines?: number;
}) {
  if (query.isPending) return <Skeleton lines={lines} />;
  if (query.error) return <ErrorBox error={query.error} onRetry={() => query.refetch()} />;
  return <>{children(query.data as T)}</>;
}

export function Notice({ tone = "info", children }: { tone?: "info" | "attention" | "success" | "danger"; children: ReactNode }) {
  const icon = tone === "success" ? "check-circle" : tone === "danger" ? "x-circle" : tone === "attention" ? "warning" : "info";
  return (
    <div className={`notice notice--${tone}`} role={tone === "danger" ? "alert" : "status"}>
      <Icon name={icon} size={20} />
      <div>{children}</div>
    </div>
  );
}
